import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Whether this cart line may be removed. Lines Shopify marks
   * `can_remove == false` (trash control hidden) keep the minimum as the minus
   * floor so the button can't decrement to 0, which the cart treats as removal.
   * @returns {boolean}
   */
  get canRemoveLine() {
    return this.dataset.canRemove !== 'false';
  }

  /**
   * Updates button states based on current value and limits.
   * Honors the server-disabled state for non-updateable lines; removable lines
   * keep minus enabled down to 0 (to remove), non-removable lines floor at min.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const floor = this.canRemoveLine ? 0 : min;

    minusButton.disabled = this.serverDisabledMinus || value <= floor;
    plusButton.disabled = this.serverDisabledPlus || (effectiveMax !== null && value >= effectiveMax);
  }

  /**
   * Steps the quantity. On a removable line, a decrement at the minimum sets the
   * value to 0 so the minus button removes the line item (the cart treats a
   * dispatched quantity of 0 as a removal). Non-removable lines and all other
   * steps defer to the base clamping behavior.
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { min, value } = this.getCurrentValues();

    if (this.canRemoveLine && stepMultiplier < 0 && value > 0 && value <= min) {
      this.refs.quantityInput.value = '0';
      this.onQuantityChange();
      this.updateButtonStates();
      return;
    }

    super.updateQuantity(stepMultiplier);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
